import React, { useCallback, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/joy";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { SectionCitation } from "../../types/citations";
import { GenerationSection } from "../../types/documentTypes";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import ReferenceItem from "./ReferenceItem";
import styles from "./SourcePanel.module.css";

interface SourcePanelProps {
  sections: GenerationSection[];
  citations: Record<number, SectionCitation[]>;
  citationsLoaded: boolean;
  validationStatus: Record<number, "validating" | "complete">;
  validationErrors?: Record<number, string>;
  expandedRef: { sectionId: number; refId: string } | null;
  onExpandRef: (sectionId: number, refId: string) => void;
  onClickRefInEditor: (sectionId: number, refId: string) => void;
  activeSectionId?: number | null;
  documentId?: number;
}

const SourcePanel: React.FC<SourcePanelProps> = ({
  sections,
  citations,
  citationsLoaded,
  validationStatus,
  validationErrors,
  expandedRef,
  onExpandRef,
  onClickRefInEditor,
  activeSectionId,
  documentId,
}) => {
  const rawFetch = useAuthFetch("raw");

  const handleExportCitations = useCallback(async () => {
    if (!documentId) return;
    try {
      const response = await rawFetch(
        `/api/workspace/meridian/documents/${documentId}/citations/export`,
        "export citation report",
      );
      const blob = await (response as Response).blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = "Citation Verification Report.docx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export citation report:", err);
    }
  }, [documentId, rawFetch]);

  const handleRefClick = useCallback(
    (sectionId: number, refId: string) => {
      onExpandRef(sectionId, refId);
    },
    [onExpandRef],
  );

  const handleClickInEditor = useCallback(
    (sectionId: number, refId: string) => {
      onClickRefInEditor(sectionId, refId);
    },
    [onClickRefInEditor],
  );

  const handleScrollToSection = useCallback((sectionId: number) => {
    const sectionEl = document.querySelector(
      `[data-section-id="${sectionId}"]`,
    );
    if (sectionEl) {
      sectionEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  // Scroll expanded reference into view
  const expandedItemRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (expandedRef && expandedItemRef.current) {
      expandedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [expandedRef]);

  // Auto-scroll active section into view
  const activeSectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (activeSectionId && activeSectionRef.current) {
      activeSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeSectionId]);

  if (!citationsLoaded) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <Typography level="title-sm">Source Verification</Typography>
        </div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            gap: 1,
          }}
        >
          <CircularProgress size="sm" />
          <Typography level="body-sm" color="neutral">
            Loading citations...
          </Typography>
        </Box>
      </div>
    );
  }

  // Filter to only non-thinking, non-empty sections
  const visibleSections = sections.filter(
    (s) => s.section_type !== "Thinking" && s.content && s.content.length > 0,
  );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography level="title-sm">Source Verification</Typography>
          {documentId && (
            <Tooltip
              title="Export verification report"
              placement="left"
              size="sm"
            >
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={handleExportCitations}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </div>
      <div className={styles.panelContent}>
        {visibleSections.map((section) => {
          const sectionCitations = citations[section.section_id] || [];
          const status = validationStatus[section.section_id];
          const sectionError = validationErrors?.[section.section_id];
          const hasNoCitations =
            sectionCitations.length === 0 &&
            status !== "validating" &&
            !sectionError;
          const isActive = activeSectionId === section.section_id;

          return (
            <div
              key={section.section_id}
              ref={isActive ? activeSectionRef : undefined}
              className={`${styles.sectionContainer} ${isActive ? styles.sectionContainerActive : ""}`}
            >
              <div className={styles.sectionTitle}>
                <Typography
                  level="body-xs"
                  fontWeight="lg"
                  sx={{ color: "neutral.600" }}
                >
                  {section.title}
                </Typography>
              </div>

              {status === "validating" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <CircularProgress size="sm" />
                  <Typography level="body-xs" color="neutral">
                    Verifying sources...
                  </Typography>
                </Box>
              )}

              {sectionError && (
                <Alert
                  size="sm"
                  color="warning"
                  variant="soft"
                  sx={{ mx: 1.5, my: 0.5 }}
                >
                  <Typography level="body-xs">{sectionError}</Typography>
                </Alert>
              )}

              {hasNoCitations && (
                <Box sx={{ px: 1.5, py: 0.5 }}>
                  <Typography
                    level="body-xs"
                    sx={{ color: "neutral.400", fontStyle: "italic" }}
                  >
                    No citations
                  </Typography>
                </Box>
              )}

              {sectionCitations.length > 0 && (
                <div className={styles.referenceList}>
                  {[...sectionCitations]
                    .sort((a, b) => a.order_index - b.order_index)
                    .map((citation) => (
                      <div
                        key={`${section.section_id}-${citation.ref_id}`}
                        ref={
                          expandedRef?.sectionId === section.section_id &&
                          expandedRef?.refId === citation.ref_id
                            ? expandedItemRef
                            : undefined
                        }
                      >
                        <ReferenceItem
                          citation={citation}
                          isExpanded={
                            expandedRef?.sectionId === section.section_id &&
                            expandedRef?.refId === citation.ref_id
                          }
                          onClick={() =>
                            handleRefClick(section.section_id, citation.ref_id)
                          }
                          onClickInEditor={() =>
                            handleClickInEditor(
                              section.section_id,
                              citation.ref_id,
                            )
                          }
                          onScrollToSection={handleScrollToSection}
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(SourcePanel);
